import { Icons } from '@/components/icons';
import { Typography } from '@/components/primitives/Typography';
import { TransText, type TransTextKeys } from '@/i18n/trans/text';
import type { BookingTripSummaryDTO } from '@/types/dto/agent';
import { format, formatTime } from '@/utils/dateTime';
import type { FC } from 'react';

interface TicketTripSummariesProps {
  title: TransTextKeys;
  tripSummaries: Array<BookingTripSummaryDTO>;
  passengerCount?: number;
}

const TicketTripSummaries: FC<TicketTripSummariesProps> = ({
  title,
  tripSummaries,
  passengerCount,
}) => {
  if (!tripSummaries.length) {
    return null;
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      <Typography variant="body1-bold" className="text-dark">
        <TransText i18nKey={title} values={{ count: tripSummaries.length }} />
      </Typography>
      {tripSummaries.map((trip) => (
        <div key={trip.id} className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2 text-primary">
            <Typography variant="body1-bold">{trip.origin?.name}</Typography>
            <Icons.arrowRight className="h-4 w-4" />
            <Typography variant="body1-bold">
              {trip.destination?.name}
            </Typography>
          </div>
          <div className="flex gap-0.5">
            {trip.origin?.time && (
              <>
                <Typography variant="body2" className="text-neutral">
                  {format(trip.origin.time, 'EEE, dd MMM yyyy')}
                </Typography>
                <Typography variant="body2" className="text-neutral">
                  &#8226;
                </Typography>
                <Typography variant="body2" className="text-neutral">
                  {formatTime(trip.origin.time)}
                </Typography>
                <Typography variant="body2" className="text-neutral">
                  &#8226;
                </Typography>
              </>
            )}
            {passengerCount && (
              <>
                <Typography variant="body2" className="text-neutral">
                  <TransText
                    i18nKey="passengersCount"
                    values={{ count: passengerCount }}
                  />
                </Typography>
                <Typography variant="body2" className="text-neutral">
                  &#8226;
                </Typography>
              </>
            )}
            {trip.transfers !== undefined && (
              <Typography variant="body2" className="text-error-text">
                <TransText
                  i18nKey="transfersCount"
                  values={{ count: trip.transfers }}
                />
              </Typography>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TicketTripSummaries;
